import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { MouseEvent, ReactNode } from 'react';

import Link from '@components/link';

import type { CommandPaletteNavigateOutcome } from './command-palette.models';
import s from './command-palette-option.scss';

export interface CommandPaletteOptionProps {
  active: boolean;
  children?: ReactNode;
  className?: string;
  id: string;
  identifier?: string;
  onActivate: () => void;
  onSelect: () => void;
  outcome: CommandPaletteNavigateOutcome;
}

/**
 * A keyboard-navigable palette option. Renders as a real link for its navigate
 * outcome so modified/middle-clicks still open a new tab, while a plain left
 * click commits the selection. Falls back to a bare identifier when no children
 * are supplied.
 */
export const CommandPaletteOption = ({
  active,
  children,
  className,
  id,
  identifier,
  onActivate,
  onSelect,
  outcome,
}: CommandPaletteOptionProps) => {
  useStyles(s);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Leave the palette open for modified/middle-clicks so Link's native
    // "open in new tab" isn't cut short.
    if (
      event.button === 0 &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey
    ) {
      onSelect();
    }
  };

  return (
    <Link
      aria-selected={active}
      className={classNames(s.option, className)}
      id={id}
      onClick={handleClick}
      onMouseEnter={onActivate}
      role="option"
      to={outcome.to}
    >
      {children ?? <span className={s.identifier}>{identifier}</span>}
    </Link>
  );
};

export default CommandPaletteOption;
